import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Phone, Mail, MapPin, Clock, Check,
  Menu, X, Heart, Send, Calendar, Leaf, Star,
} from 'lucide-react';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const SEASONS = [
  { name: 'Late Winter',  months: [0, 1] },
  { name: 'Early Spring', months: [2, 3] },
  { name: 'Late Spring',  months: [4] },
  { name: 'Early Summer', months: [5, 6] },
  { name: 'High Summer',  months: [7] },
  { name: 'Early Autumn', months: [8, 9] },
  { name: 'Late Autumn',  months: [10] },
  { name: 'Winter',       months: [11] },
];

const STEMS = {
  rose:        { name: 'Garden Rose',  color: '#E5B89A', dark: '#A04E3F' },
  peony:       { name: 'Peony',        color: '#F1B4B0', dark: '#C97064' },
  dahlia:      { name: 'Dahlia',       color: '#D9543E', dark: '#7C2A1F' },
  ranunculus:  { name: 'Ranunculus',   color: '#F2DCC2', dark: '#C49B72' },
  sweetpea:    { name: 'Sweet Pea',    color: '#C9A9D1', dark: '#8B6B95' },
  foxglove:    { name: 'Foxglove',     color: '#C77D9C', dark: '#7C4364' },
  anemone:     { name: 'Anemone',      color: '#F4ECE0', dark: '#1F1410' },
  lisianthus:  { name: 'Lisianthus',   color: '#D8C5DD', dark: '#6E5475' },
  tulip:       { name: 'Tulip',        color: '#D9748D', dark: '#9A3F58' },
  sunflower:   { name: 'Sunflower',    color: '#F2C94C', dark: '#7A4F1A' },
  euc:         { name: 'Eucalyptus',   color: '#A6B49C', dark: '#566F58' },
  fern:        { name: 'Fern',         color: '#7C8771', dark: '#3E4A3D' },
  branches:    { name: 'Branches',     color: '#B89A7C', dark: '#5A4530' },
  ammi:        { name: 'Ammi (Lace)',  color: '#FFFFFF', dark: '#A6B49C' },
};

const BLOOM_CALENDAR = [
  ['ranunculus', 'anemone', 'branches', 'euc'],                   // Jan
  ['ranunculus', 'anemone', 'tulip', 'branches'],                  // Feb
  ['tulip', 'ranunculus', 'anemone', 'fern'],                      // Mar
  ['tulip', 'sweetpea', 'fern', 'ammi'],                           // Apr
  ['peony', 'sweetpea', 'foxglove', 'fern', 'ammi'],               // May
  ['peony', 'rose', 'foxglove', 'sweetpea', 'lisianthus'],         // Jun
  ['rose', 'lisianthus', 'foxglove', 'ammi', 'fern'],              // Jul
  ['dahlia', 'sunflower', 'lisianthus', 'ammi', 'rose'],           // Aug
  ['dahlia', 'sunflower', 'rose', 'fern', 'branches'],             // Sep
  ['dahlia', 'rose', 'branches', 'fern'],                          // Oct
  ['anemone', 'branches', 'euc', 'fern'],                          // Nov
  ['ranunculus', 'anemone', 'branches', 'euc'],                    // Dec
];

const FARMS = [
  { id: 1,  city: 'Hillsboro, OR',   name: 'Peony Hill',         signature: 'Heritage peonies, May–June',     x: 220, y: 280 },
  { id: 2,  city: 'Sandy, OR',       name: 'Cascade Dahlia',     signature: 'Cafe au Lait dahlias, Jul–Oct',  x: 270, y: 290 },
  { id: 3,  city: 'Tillamook, OR',   name: 'Fern Hollow',        signature: 'Foraged greens, year-round',     x: 130, y: 290 },
  { id: 4,  city: 'Hood River, OR',  name: 'Orchard Bloom',      signature: 'Branches & fruiting blossom',    x: 320, y: 280 },
  { id: 5,  city: 'Salem, OR',       name: 'Greenleaf Acres',    signature: 'Sweet peas & lisianthus',        x: 215, y: 350 },
  { id: 6,  city: 'Eugene, OR',      name: 'Field & Stem',       signature: 'Mixed cuts, Apr–Sep',            x: 220, y: 410 },
  { id: 7,  city: 'Bend, OR',        name: 'High Desert Garden', signature: 'Lavender, herbs, dry textures',  x: 340, y: 380 },
  { id: 8,  city: 'Portland, OR',    name: 'Stoop Roses',        signature: 'Urban garden roses, May–Oct',    x: 240, y: 250 },
  { id: 9,  city: 'Vancouver, WA',   name: 'River Petal',        signature: 'Anemone & ranunculus',           x: 250, y: 220 },
  { id: 10, city: 'Battle Ground, WA', name: 'Wild Acre',        signature: 'Foxglove & snapdragon',          x: 260, y: 195 },
  { id: 11, city: 'Centralia, WA',   name: 'Spring Valley',      signature: 'Tulips & narcissus',             x: 240, y: 165 },
  { id: 12, city: 'Olympia, WA',     name: 'Mossbloom',          signature: 'Mosses & foraged greens',        x: 215, y: 140 },
  { id: 13, city: 'Yakima, WA',      name: 'Sunseed Farm',       signature: 'Sunflowers & zinnia',            x: 365, y: 145 },
  { id: 14, city: 'Bellingham, WA',  name: 'Foglands Floral',    signature: 'Cool-weather mix, AprilNov',     x: 215, y: 50  },
];

const WEDDING_LOOKS = [
  { tone: 'Garden Romantic',      palette: ['#F1B4B0', '#E5B89A', '#A6B49C', '#FFFFFF'], image: 1, span: 'lg' },
  { tone: 'Burgundy & Bronze',    palette: ['#7C2A1F', '#A04E3F', '#B89A7C', '#566F58'], image: 0, span: 'md' },
  { tone: 'Soft Pastel',          palette: ['#F2DCC2', '#D8C5DD', '#A6B49C', '#FFFFFF'], image: 3, span: 'sm' },
  { tone: 'Sun-Soaked',           palette: ['#F2C94C', '#E5B89A', '#7C8771', '#FFFFFF'], image: 2, span: 'md' },
];

const SUBSCRIPTIONS = [
  { id: 'petite',   name: 'Petite Bouquet',    desc: 'Hand-tied seasonal stems, ~6 varieties.',                price: 48,  pop: false },
  { id: 'designer', name: 'Designer Bouquet',  desc: 'Lush, asymmetric, ~12 varieties + foraged greens.',      price: 88,  pop: true },
  { id: 'statement',name: 'Statement',         desc: 'In a glass vessel — designer\'s choice. The "wow" gift.', price: 165, pop: false },
];

const WORKSHOPS = [
  { date: 'Sat, May 17',  title: 'Spring Hand-Tied Bouquet', open: 4, total: 12, full: false },
  { date: 'Sat, Jun 14',  title: 'Garden Rose Crown',         open: 0, total: 8,  full: true  },
  { date: 'Sat, Jul 12',  title: 'Foraged Tablescape',        open: 7, total: 12, full: false },
  { date: 'Sat, Aug 9',   title: 'Dahlia Centerpiece',        open: 11, total: 12, full: false },
];

const STYLE = `
  .ws-display { font-family: 'Cormorant Garamond', 'Times New Roman', serif; }
  .ws-display-italic { font-family: 'Cormorant Garamond', serif; font-style: italic; font-weight: 500; }
  .ws-eyebrow { font-family: 'Cormorant Garamond', serif; font-style: italic; font-weight: 500; letter-spacing: 0.04em; }
  .ws-uppercase { letter-spacing: 0.28em; text-transform: uppercase; font-size: 0.7rem; font-weight: 600; }

  .ws-paper {
    background-color: #F8F4ED;
    background-image:
      radial-gradient(circle at 0% 0%, rgba(160,78,63,0.07), transparent 35%),
      radial-gradient(circle at 100% 100%, rgba(166,180,156,0.10), transparent 40%);
  }

  .ws-watercolor::before {
    content: '';
    position: absolute; inset: 0;
    background-image:
      radial-gradient(circle at 30% 20%, rgba(201,112,100,0.10), transparent 30%),
      radial-gradient(circle at 80% 70%, rgba(166,180,156,0.10), transparent 35%),
      radial-gradient(circle at 60% 40%, rgba(241,180,176,0.08), transparent 25%);
    pointer-events: none;
  }

  .ws-divider { width: 60px; height: 1px; background: #A04E3F; }
  .ws-divider-vertical { width: 1px; height: 40px; background: #A04E3F; }

  .ws-stem-leaf { animation: ws-sway 6s ease-in-out infinite; transform-origin: bottom center; }
  @keyframes ws-sway {
    0%, 100% { transform: rotate(-1.5deg); }
    50%      { transform: rotate(1.5deg); }
  }

  .ws-border-bottom-fade {
    background-image: linear-gradient(to right, transparent, #A04E3F, transparent);
    height: 1px;
  }

  .ws-pin-pulse::after {
    content: '';
    position: absolute; inset: -4px;
    border: 2px solid #A04E3F;
    border-radius: 50%;
    animation: ws-pulse 2.4s ease-out infinite;
  }
  @keyframes ws-pulse {
    0%   { transform: scale(0.6); opacity: 1; }
    100% { transform: scale(2.0); opacity: 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .ws-stem-leaf, .ws-pin-pulse::after { animation: none !important; }
  }
`;

function seasonForMonth(m) {
  return SEASONS.find((s) => s.months.includes(m))?.name || 'In Season';
}

function WildStemFloralDemo({ demo, sample = false }) {
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const currentMonth = new Date().getMonth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const phoneRaw = demo.contact.phone.replace(/[^\d+]/g, '');

  return (
    <div className="ws-paper text-[#1F2A1A] min-h-screen" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <style>{STYLE}</style>

      <SudcoBanner sample={sample} demoName={demo.name} demoIndustry={demo.industry} />
      <Header
        demo={demo}
        scrolled={scrolled}
        navOpen={navOpen}
        setNavOpen={setNavOpen}
        phoneRaw={phoneRaw}
      />

      <Hero demo={demo} currentMonth={currentMonth} />
      <SeasonRibbon month={currentMonth} />
      <BloomCalendar initialMonth={currentMonth} />
      <StudioBuilder />
      <OurFarms />
      <Story about={demo.about} cover={demo.gallery?.[0] || demo.cover} />
      <WeddingPortfolio gallery={demo.gallery} />
      <Services />
      <Workshops />
      <Sympathy />
      <Testimonials testimonials={demo.testimonials} />
      <Visit demo={demo} phoneRaw={phoneRaw} />
      <Footer demo={demo} phoneRaw={phoneRaw} />
    </div>
  );
}

function SudcoBanner({ sample, demoName, demoIndustry }) {
  return (
    <div className="fixed bottom-5 right-5 z-50">
      <div className="bg-[#0B0F19]/90 border border-[#2A3142] backdrop-blur-md rounded-full px-5 py-3 shadow-[0_0_60px_-15px_rgba(99,102,241,0.5)] flex items-center gap-4 text-sm">
        <span className="text-[#8B92A8] hidden sm:inline">
          {sample
            ? <>Like this? Get one for your <span className="text-[#F5F6FA] font-medium">{demoIndustry || 'business'}</span> — <span className="text-[#6366F1] font-semibold">$800</span></>
            : <>Free demo for <span className="text-[#F5F6FA] font-medium">{demoName}</span> by <span className="text-[#6366F1] font-semibold">Sudco Solutions</span></>
          }
        </span>
        <Link to="/" className="flex items-center gap-1.5 text-[#F5F6FA] font-semibold hover:text-[#06B6D4] transition-colors whitespace-nowrap">
          <ArrowLeft size={14} /> Back to Sudco
        </Link>
      </div>
    </div>
  );
}

function Header({ demo, scrolled, navOpen, setNavOpen, phoneRaw }) {
  const links = [
    { href: '#calendar',   label: 'In Season' },
    { href: '#studio',     label: 'The Studio' },
    { href: '#farms',      label: 'Our Farms' },
    { href: '#weddings',   label: 'Weddings' },
    { href: '#services',   label: 'Subscriptions' },
    { href: '#workshops',  label: 'Workshops' },
  ];

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled ? 'bg-[#F8F4ED]/95 backdrop-blur border-b border-[#E0D6C2]' : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-3">
          <WildStemMark />
          <div className="leading-tight">
            <div className="ws-display text-lg md:text-xl font-medium tracking-wide" style={{ color: '#1F2A1A' }}>
              {demo.name}
            </div>
            <div className="ws-eyebrow text-xs" style={{ color: '#A04E3F' }}>
              Portland, Oregon
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7 ws-display text-[15px]" style={{ color: '#1F2A1A' }}>
          {links.map((l) => (
            <a key={l.href} href={l.href} className="relative group hover:text-[#A04E3F] transition-colors">
              {l.label}
              <span className="absolute left-0 -bottom-1 h-px w-full origin-left scale-x-0 bg-[#A04E3F] transition-transform duration-300 group-hover:scale-x-100" />
            </a>
          ))}
        </nav>

        <a
          href={`tel:${phoneRaw}`}
          className="hidden md:inline-flex ws-display text-[15px] font-medium hover:text-[#A04E3F] transition-colors items-center gap-1.5"
          style={{ color: '#1F2A1A' }}
        >
          <Phone size={14} /> {demo.contact.phone}
        </a>

        <button
          type="button"
          onClick={() => setNavOpen((v) => !v)}
          aria-label={navOpen ? 'Close menu' : 'Open menu'}
          className="lg:hidden p-2 -mr-2"
        >
          {navOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {navOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden border-t border-[#E0D6C2] bg-[#F8F4ED]"
          >
            <div className="px-4 py-4 flex flex-col gap-1 ws-display">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setNavOpen(false)}
                  className="px-3 py-2.5 rounded-md font-medium hover:bg-[#EDE1CC]"
                  style={{ color: '#1F2A1A' }}
                >
                  {l.label}
                </a>
              ))}
              <a
                href={`tel:${phoneRaw}`}
                className="mt-2 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md font-semibold text-white"
                style={{ background: '#A04E3F' }}
              >
                <Phone size={14} /> {demo.contact.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function WildStemMark() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
      <circle cx="20" cy="20" r="18" fill="none" stroke="#A04E3F" strokeWidth="0.7" />
      <path d="M 20 12 Q 14 18 14 24 Q 14 28 20 28 Q 26 28 26 24 Q 26 18 20 12 Z" fill="#A6B49C" />
      <path d="M 20 14 L 20 28" stroke="#566F58" strokeWidth="0.6" />
      <circle cx="20" cy="14" r="3" fill="#C97064" />
      <text x="20" y="36" textAnchor="middle" fontSize="3.5" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fill="#1F2A1A" letterSpacing="0.5">
        WS
      </text>
    </svg>
  );
}

function Hero({ demo, currentMonth }) {
  const seasonName = seasonForMonth(currentMonth);

  return (
    <section id="top" className="relative overflow-hidden ws-watercolor">
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-10 md:pt-16 pb-16 md:pb-24">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="ws-divider" />
              <span className="ws-uppercase" style={{ color: '#A04E3F' }}>
                {seasonName} · {MONTHS[currentMonth]}
              </span>
            </div>

            <h1 className="ws-display text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.98] tracking-tight font-light" style={{ color: '#1F2A1A' }}>
              Foraged,
              <br />
              <span className="ws-display-italic" style={{ color: '#A04E3F' }}>farm-direct,</span>
              <br />
              designed by hand.
            </h1>

            <p className="mt-7 max-w-md text-base md:text-lg leading-relaxed" style={{ color: '#3E4A3D' }}>
              {demo.tagline}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="#studio"
                className="inline-flex items-center gap-2 px-7 py-3.5 ws-display text-[15px] font-medium transition-all hover:bg-[#7C2A1F]"
                style={{ background: '#A04E3F', color: '#F8F4ED', borderRadius: 0 }}
              >
                Build a bouquet <ArrowRight size={16} />
              </a>
              <a
                href="#weddings"
                className="ws-display-italic text-lg hover:underline" style={{ color: '#A04E3F' }}
              >
                or see weddings &rarr;
              </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm" style={{ color: '#7C8771' }}>
              <span className="flex items-center gap-1.5"><Star size={14} className="fill-[#A04E3F] text-[#A04E3F]" /> 4.9 · 80+ weddings a year</span>
              <span className="flex items-center gap-1.5"><Leaf size={14} /> 14 PNW farm partners</span>
              <span className="flex items-center gap-1.5"><Heart size={14} /> Same-day in Portland</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={demo.cover} alt="" className="w-full h-full object-cover" fetchpriority="high" />
              <div className="absolute inset-0 ring-1 ring-inset ring-black/5" />
            </div>

            <div className="absolute -bottom-8 -left-6 sm:-left-10 bg-[#F8F4ED] px-5 py-4 max-w-[260px] shadow-lg" style={{ border: '1px solid #E0D6C2' }}>
              <p className="ws-eyebrow text-xs" style={{ color: '#A04E3F' }}>Today's bouquet</p>
              <p className="ws-display-italic text-2xl leading-tight mt-1" style={{ color: '#1F2A1A' }}>
                Garden roses, lisianthus, fern fronds.
              </p>
            </div>

            <div className="absolute -top-4 -right-3 sm:-right-6 bg-[#F8F4ED] px-4 py-3 shadow-md" style={{ border: '1px solid #E0D6C2' }}>
              <p className="ws-eyebrow text-xs" style={{ color: '#A04E3F' }}>Cut at dawn</p>
              <p className="ws-display-italic text-lg" style={{ color: '#1F2A1A' }}>Peony Hill, Hillsboro.</p>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="ws-border-bottom-fade max-w-5xl mx-auto" />
    </section>
  );
}

function SeasonRibbon({ month }) {
  const stems = BLOOM_CALENDAR[month];
  return (
    <section className="py-10 md:py-12 overflow-hidden border-b" style={{ borderColor: '#E0D6C2' }}>
      <div className="max-w-6xl mx-auto px-4 md:px-6 flex flex-col md:flex-row items-center justify-center gap-4 md:gap-10 text-center">
        <span className="ws-eyebrow" style={{ color: '#A04E3F' }}>This week we are cutting</span>
        <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 ws-display-italic text-2xl md:text-3xl" style={{ color: '#1F2A1A' }}>
          {stems.map((id, i) => (
            <span key={id + i} className="inline-flex items-center gap-2.5">
              <StemSVG type={id} size={28} />
              {STEMS[id].name}
              {i < stems.length - 1 && <span className="text-[#A04E3F] mx-2">&middot;</span>}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function StemSVG({ type, size = 32 }) {
  // Simple inline SVG illustration of each stem. Each fits a 32x40 bounding
  // box with the bloom on top and a short stem dangling for visual variety.
  const s = STEMS[type];
  if (!s) return null;
  const w = size;
  const h = size * 1.25;
  return (
    <svg width={w} height={h} viewBox="0 0 32 40" aria-hidden="true">
      {renderStemMark(type, s)}
    </svg>
  );
}

function renderStemMark(type, s) {
  const stem = <line x1="16" y1="22" x2="16" y2="38" stroke="#566F58" strokeWidth="1" />;
  switch (type) {
    case 'rose':
      return (
        <g>
          {stem}
          <circle cx="16" cy="14" r="9" fill={s.color} />
          <circle cx="16" cy="14" r="6" fill="#F0CFAB" />
          <circle cx="16" cy="14" r="3" fill={s.dark} />
        </g>
      );
    case 'peony':
      return (
        <g>
          {stem}
          <circle cx="11" cy="14" r="6" fill={s.color} />
          <circle cx="20" cy="13" r="6" fill={s.color} />
          <circle cx="16" cy="9"  r="6" fill={s.color} />
          <circle cx="16" cy="17" r="5" fill="#FAD7D5" />
          <circle cx="16" cy="14" r="2.5" fill={s.dark} />
        </g>
      );
    case 'dahlia':
      return (
        <g>
          {stem}
          {Array.from({ length: 12 }).map((_, i) => {
            const a = (i * 30) * Math.PI / 180;
            const x1 = 16 + Math.cos(a) * 2;
            const y1 = 14 + Math.sin(a) * 2;
            const x2 = 16 + Math.cos(a) * 9;
            const y2 = 14 + Math.sin(a) * 9;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={s.color} strokeWidth="2.5" strokeLinecap="round" />;
          })}
          <circle cx="16" cy="14" r="3" fill={s.dark} />
        </g>
      );
    case 'ranunculus':
      return (
        <g>
          {stem}
          <circle cx="16" cy="15" r="7" fill={s.color} />
          <circle cx="16" cy="15" r="5" fill="#FAEFD9" />
          <circle cx="16" cy="15" r="3" fill={s.dark} opacity="0.55" />
          <circle cx="16" cy="15" r="1.5" fill={s.dark} />
        </g>
      );
    case 'sweetpea':
      return (
        <g>
          {stem}
          <ellipse cx="11" cy="13" rx="5" ry="4" fill={s.color} transform="rotate(-25 11 13)" />
          <ellipse cx="21" cy="13" rx="5" ry="4" fill={s.color} transform="rotate(25 21 13)" />
          <ellipse cx="16" cy="17" rx="4" ry="3" fill={s.dark} opacity="0.7" />
        </g>
      );
    case 'foxglove':
      return (
        <g>
          <line x1="16" y1="38" x2="16" y2="6" stroke="#566F58" strokeWidth="1" />
          <ellipse cx="13" cy="9"  rx="3" ry="2.5" fill={s.color} transform="rotate(-30 13 9)" />
          <ellipse cx="20" cy="13" rx="3" ry="2.5" fill={s.color} transform="rotate(30 20 13)" />
          <ellipse cx="13" cy="17" rx="3" ry="2.5" fill={s.color} transform="rotate(-30 13 17)" />
          <ellipse cx="20" cy="21" rx="3" ry="2.5" fill={s.color} transform="rotate(30 20 21)" />
          <ellipse cx="13" cy="25" rx="3" ry="2.5" fill={s.color} transform="rotate(-30 13 25)" />
        </g>
      );
    case 'anemone':
      return (
        <g>
          {stem}
          {Array.from({ length: 6 }).map((_, i) => {
            const a = (i * 60 - 90) * Math.PI / 180;
            const cx = 16 + Math.cos(a) * 5;
            const cy = 14 + Math.sin(a) * 5;
            return <ellipse key={i} cx={cx} cy={cy} rx="4" ry="3" fill={s.color} transform={`rotate(${i * 60} ${cx} ${cy})`} />;
          })}
          <circle cx="16" cy="14" r="3" fill={s.dark} />
        </g>
      );
    case 'lisianthus':
      return (
        <g>
          {stem}
          {Array.from({ length: 5 }).map((_, i) => {
            const a = (i * 72 - 90) * Math.PI / 180;
            const cx = 16 + Math.cos(a) * 4;
            const cy = 14 + Math.sin(a) * 4;
            return <ellipse key={i} cx={cx} cy={cy} rx="4.5" ry="3.5" fill={s.color} transform={`rotate(${i * 72 + 90} ${cx} ${cy})`} />;
          })}
          <circle cx="16" cy="14" r="2.5" fill={s.dark} opacity="0.6" />
        </g>
      );
    case 'tulip':
      return (
        <g>
          {stem}
          <path d="M 9 15 Q 9 7 16 7 Q 23 7 23 15 Q 21 18 16 18 Q 11 18 9 15 Z" fill={s.color} />
          <path d="M 13 8 Q 16 10 19 8 Q 19 14 16 16 Q 13 14 13 8 Z" fill={s.dark} opacity="0.5" />
          <path d="M 10 22 Q 4 26 8 36" stroke={STEMS.fern.dark} strokeWidth="1" fill="none" />
        </g>
      );
    case 'sunflower':
      return (
        <g>
          {stem}
          {Array.from({ length: 14 }).map((_, i) => {
            const a = (i * (360 / 14)) * Math.PI / 180;
            const cx = 16 + Math.cos(a) * 5.5;
            const cy = 14 + Math.sin(a) * 5.5;
            return <ellipse key={i} cx={cx} cy={cy} rx="3" ry="2" fill={s.color} transform={`rotate(${(i * 360 / 14) + 90} ${cx} ${cy})`} />;
          })}
          <circle cx="16" cy="14" r="3.5" fill={s.dark} />
        </g>
      );
    case 'euc':
      return (
        <g>
          {stem}
          <circle cx="11" cy="10" r="3" fill={s.color} />
          <circle cx="20" cy="14" r="3" fill={s.color} />
          <circle cx="13" cy="18" r="3" fill={s.color} />
          <circle cx="21" cy="22" r="2.5" fill={s.color} />
          <circle cx="14" cy="26" r="2.5" fill={s.color} />
        </g>
      );
    case 'fern':
      return (
        <g>
          <line x1="16" y1="38" x2="16" y2="4" stroke={s.dark} strokeWidth="1" />
          {Array.from({ length: 7 }).map((_, i) => {
            const y = 6 + i * 4;
            const len = 8 - i * 0.6;
            return (
              <g key={i}>
                <path d={`M 16 ${y} Q ${16 - len} ${y - 2} ${16 - len - 1} ${y + 1}`} fill="none" stroke={s.color} strokeWidth="1.5" />
                <path d={`M 16 ${y} Q ${16 + len} ${y - 2} ${16 + len + 1} ${y + 1}`} fill="none" stroke={s.color} strokeWidth="1.5" />
              </g>
            );
          })}
        </g>
      );
    case 'branches':
      return (
        <g>
          <line x1="16" y1="38" x2="14" y2="6" stroke={s.dark} strokeWidth="1.2" />
          <line x1="14" y1="14" x2="22" y2="10" stroke={s.dark} strokeWidth="1" />
          <line x1="14" y1="22" x2="22" y2="20" stroke={s.dark} strokeWidth="1" />
          <line x1="14" y1="30" x2="9"  y2="28" stroke={s.dark} strokeWidth="1" />
          <circle cx="22" cy="10" r="1.6" fill={s.color} />
          <circle cx="22" cy="20" r="1.6" fill={s.color} />
          <circle cx="9"  cy="28" r="1.6" fill={s.color} />
          <circle cx="14" cy="6"  r="1.6" fill={s.color} />
        </g>
      );
    case 'ammi':
      return (
        <g>
          {stem}
          {Array.from({ length: 18 }).map((_, i) => {
            const a = (i * 20) * Math.PI / 180;
            const r = 3 + (i % 3) * 2.5;
            const cx = 16 + Math.cos(a) * r;
            const cy = 14 + Math.sin(a) * r;
            return <circle key={i} cx={cx} cy={cy} r="0.8" fill={s.color} stroke={s.dark} strokeWidth="0.3" />;
          })}
          <circle cx="16" cy="14" r="0.7" fill={s.dark} />
        </g>
      );
    default:
      return stem;
  }
}

function BloomCalendar({ initialMonth }) {
  const [month, setMonth] = useState(initialMonth);
  const trackRef = useRef(null);
  const reduce = useReducedMotion();
  const stems = BLOOM_CALENDAR[month];
  const isCurrent = month === initialMonth;

  function handleTrack(e) {
    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const fraction = Math.max(0, Math.min(1, x / rect.width));
    const m = Math.round(fraction * 11);
    setMonth(m);
  }

  function handleKey(e) {
    if (e.key === 'ArrowLeft')  setMonth((m) => (m + 11) % 12);
    if (e.key === 'ArrowRight') setMonth((m) => (m + 1) % 12);
  }

  return (
    <section id="calendar" className="relative py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <p className="ws-eyebrow mb-2" style={{ color: '#A04E3F' }}>The Bloom Calendar</p>
          <h2 className="ws-display text-4xl md:text-6xl leading-tight font-light max-w-3xl mx-auto" style={{ color: '#1F2A1A' }}>
            What's <span className="ws-display-italic" style={{ color: '#A04E3F' }}>actually in season</span>
            <br /> in the Pacific Northwest.
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-base md:text-lg" style={{ color: '#3E4A3D' }}>
            Drag along the year. We never fly stems in from South America just to fake summer in February.
          </p>
        </div>

        <div className="mb-12">
          <div
            ref={trackRef}
            className="relative h-16 cursor-pointer select-none"
            onClick={handleTrack}
            onKeyDown={handleKey}
            role="slider"
            aria-label="Month selector"
            aria-valuemin={0}
            aria-valuemax={11}
            aria-valuenow={month}
            aria-valuetext={MONTHS[month]}
            tabIndex={0}
          >
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px" style={{ background: '#E0D6C2' }} />
            <div className="relative h-full">
              {MONTHS_SHORT.map((m, i) => {
                const left = (i / 11) * 100;
                const isActive = i === month;
                const isInitial = i === initialMonth;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setMonth(i); }}
                    className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 group"
                    style={{ left: `${left}%` }}
                    aria-label={MONTHS[i]}
                  >
                    <span
                      className={`block w-2 h-2 rounded-full transition-all ${isActive ? 'scale-150' : ''}`}
                      style={{ background: isActive ? '#A04E3F' : isInitial ? '#A04E3F' : '#A6B49C', opacity: isActive ? 1 : isInitial ? 0.5 : 0.6 }}
                    />
                    <span
                      className={`absolute left-1/2 -translate-x-1/2 mt-3 text-xs uppercase tracking-widest font-semibold transition-colors whitespace-nowrap ${
                        isActive ? '' : 'opacity-50 group-hover:opacity-100'
                      }`}
                      style={{ color: isActive ? '#A04E3F' : '#1F2A1A' }}
                    >
                      {m}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-[auto_1fr] gap-8 md:gap-14 items-start">
          <div className="md:border-r md:pr-12 md:min-w-[220px]" style={{ borderColor: '#E0D6C2' }}>
            <p className="ws-eyebrow text-sm" style={{ color: '#A04E3F' }}>
              {seasonForMonth(month)}
            </p>
            <p className="ws-display text-5xl md:text-6xl font-light mt-1" style={{ color: '#1F2A1A' }}>
              {MONTHS[month]}
            </p>
            {isCurrent && (
              <p className="ws-display-italic text-lg mt-2" style={{ color: '#A04E3F' }}>
                — that's right now.
              </p>
            )}
          </div>

          <div>
            <p className="ws-eyebrow text-xs mb-5" style={{ color: '#7C8771' }}>
              In the studio this month
            </p>
            <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
              {stems.map((id, i) => (
                <motion.li
                  key={`${month}-${id}-${i}`}
                  initial={reduce ? false : { opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: i * 0.05 }}
                  className="flex items-start gap-3"
                >
                  <span className="flex-shrink-0">
                    <StemSVG type={id} size={36} />
                  </span>
                  <span>
                    <span className="ws-display text-xl block leading-tight" style={{ color: '#1F2A1A' }}>
                      {STEMS[id].name}
                    </span>
                    <span className="text-xs uppercase tracking-wider" style={{ color: '#7C8771' }}>
                      Field-cut
                    </span>
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function StudioBuilder() {
  const [stems, setStems] = useState([]); // ordered list of stem ids placed
  const [submitted, setSubmitted] = useState(false);

  const palette = useMemo(() => ['rose', 'peony', 'dahlia', 'ranunculus', 'lisianthus', 'sweetpea', 'anemone', 'euc', 'fern', 'ammi'], []);

  function addStem(id) {
    setStems((s) => (s.length >= 14 ? s : [...s, id]));
  }
  function removeLast() {
    setStems((s) => s.slice(0, -1));
  }
  function clear() { setStems([]); }

  function submit(e) {
    e.preventDefault();
    if (stems.length === 0) return;
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4500);
  }

  const counts = useMemo(() => {
    return stems.reduce((acc, id) => { acc[id] = (acc[id] || 0) + 1; return acc; }, {});
  }, [stems]);

  return (
    <section id="studio" className="relative py-20 md:py-28" style={{ background: '#1F2A1A', color: '#F4ECE0' }}>
      <div className="absolute inset-0 opacity-30 pointer-events-none"
           style={{
             backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(241,180,176,0.15), transparent 30%), radial-gradient(circle at 80% 70%, rgba(166,180,156,0.15), transparent 35%)',
           }} />

      <div className="relative max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <p className="ws-eyebrow mb-2" style={{ color: '#F1B4B0' }}>The Studio</p>
          <h2 className="ws-display text-4xl md:text-6xl leading-tight font-light">
            Tie a bouquet — <span className="ws-display-italic" style={{ color: '#F1B4B0' }}>see it as you go.</span>
          </h2>
          <p className="mt-4 max-w-xl mx-auto opacity-80 leading-relaxed">
            Click stems to add them to the bouquet. We'll quote it from what's actually in season this week.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-16 items-center">
          <div className="flex justify-center">
            <BouquetSVG stems={stems} />
          </div>

          <div>
            <p className="ws-eyebrow text-xs mb-3" style={{ color: '#F1B4B0' }}>
              The palette · {Object.keys(counts).length} type{Object.keys(counts).length === 1 ? '' : 's'} · {stems.length} stems
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
              {palette.map((id) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => addStem(id)}
                  disabled={stems.length >= 14}
                  className="flex items-center gap-2 px-3 py-2.5 border text-left transition-all hover:bg-[#F1B4B0]/10 hover:border-[#F1B4B0] disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ borderColor: 'rgba(244,236,224,0.2)' }}
                >
                  <span className="flex-shrink-0 -mt-1">
                    <StemSVG type={id} size={26} />
                  </span>
                  <span className="ws-display text-base leading-tight">{STEMS[id].name}</span>
                  {counts[id] ? (
                    <span className="ml-auto text-xs px-1.5 py-0.5 rounded-full" style={{ background: '#F1B4B0', color: '#1F2A1A' }}>
                      {counts[id]}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 mb-7">
              <button
                type="button"
                onClick={removeLast}
                disabled={stems.length === 0}
                className="ws-display text-sm hover:underline disabled:opacity-40"
                style={{ color: '#F1B4B0' }}
              >
                ← Remove last
              </button>
              <span className="opacity-30">·</span>
              <button
                type="button"
                onClick={clear}
                disabled={stems.length === 0}
                className="ws-display text-sm hover:underline disabled:opacity-40"
                style={{ color: '#F1B4B0' }}
              >
                Start over
              </button>
            </div>

            <form onSubmit={submit}>
              <button
                type="submit"
                disabled={stems.length === 0 || submitted}
                className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 ws-display text-lg font-medium transition-all hover:bg-[#7C2A1F] disabled:opacity-50"
                style={{ background: '#A04E3F', color: '#F8F4ED', borderRadius: 0 }}
              >
                {submitted ? (
                  <><Check size={18} /> We'll send a quote within the day</>
                ) : stems.length === 0 ? (
                  <>Pick a stem to start</>
                ) : (
                  <>Send for a quote <Send size={18} /></>
                )}
              </button>
              <p className="ws-eyebrow text-xs text-center mt-3 opacity-70">
                Free. No card needed.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function BouquetSVG({ stems }) {
  // 14 slots radiating up from a single tie point, ordered back-to-front
  // so SVG draw order produces correct depth. Each stem rotates around its
  // own tip (16, 38) so all stems converge at TIP_X/TIP_Y.
  const TIP_X = 150;
  const TIP_Y = 232;
  const slots = [
    { angle: 0,    scale: 1.4, z: 1 },
    { angle: -55,  scale: 0.9, z: 1 },
    { angle: 55,   scale: 0.9, z: 1 },
    { angle: -30,  scale: 1.2, z: 2 },
    { angle: 30,   scale: 1.2, z: 2 },
    { angle: -15,  scale: 1.1, z: 3 },
    { angle: 15,   scale: 1.1, z: 3 },
    { angle: -42,  scale: 1.0, z: 3 },
    { angle: 42,   scale: 1.0, z: 3 },
    { angle: 0,    scale: 1.0, z: 4 },
    { angle: -22,  scale: 0.95, z: 4 },
    { angle: 22,   scale: 0.95, z: 4 },
    { angle: -68,  scale: 0.8, z: 5 },
    { angle: 68,   scale: 0.8, z: 5 },
  ];

  return (
    <div className="relative">
      <svg viewBox="0 0 300 360" className="w-72 md:w-[360px] h-auto" aria-label="Bouquet preview">
        {[...Array(6)].map((_, i) => {
          const dx = (i - 2.5) * 8;
          return (
            <line
              key={i}
              x1={150 + dx * 0.3}
              y1={254}
              x2={150 + dx}
              y2={300}
              stroke="#566F58"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          );
        })}

        {stems.map((id, i) => {
          if (i >= slots.length) return null;
          const slot = slots[i];
          return (
            <g
              key={i}
              transform={`translate(${TIP_X - 16 * slot.scale}, ${TIP_Y - 38 * slot.scale}) scale(${slot.scale}) rotate(${slot.angle}, 16, 38)`}
            >
              <motion.g
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.04 }}
                style={{ filter: `drop-shadow(0 ${slot.z}px ${slot.z}px rgba(0,0,0,0.25))` }}
              >
                {renderStemMark(id, STEMS[id])}
              </motion.g>
            </g>
          );
        })}

        <ellipse cx="150" cy="244" rx="36" ry="13" fill="#A04E3F" />
        <ellipse cx="150" cy="244" rx="36" ry="13" fill="none" stroke="#7C2A1F" strokeWidth="0.6" />
        <path d="M 150 244 Q 145 248 142 252 Q 148 251 152 248 Q 156 251 162 252 Q 159 248 154 244 Z" fill="#7C2A1F" opacity="0.4" />
        <line x1="128" y1="256" x2="120" y2="298" stroke="#A04E3F" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="172" y1="256" x2="180" y2="302" stroke="#A04E3F" strokeWidth="2.5" strokeLinecap="round" />
        <path d="M 120 298 Q 115 302 117 308 Q 122 306 124 302 Z" fill="#A04E3F" />
        <path d="M 180 302 Q 185 306 183 312 Q 178 310 176 306 Z" fill="#A04E3F" />

        {stems.length === 0 && (
          <g>
            <circle cx="150" cy="200" r="50" fill="none" stroke="rgba(244,236,224,0.18)" strokeWidth="1.5" strokeDasharray="4 6" />
            <text x="150" y="205" textAnchor="middle" fontSize="14" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fill="rgba(244,236,224,0.5)">
              your bouquet, here
            </text>
          </g>
        )}
      </svg>
      <p className="ws-eyebrow text-xs text-center mt-2 opacity-70">Hand-tied · live preview</p>
    </div>
  );
}

function OurFarms() {
  const [active, setActive] = useState(1);
  const farm = FARMS.find((f) => f.id === active) || FARMS[0];

  return (
    <section id="farms" className="relative py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <p className="ws-eyebrow mb-2" style={{ color: '#A04E3F' }}>Where the stems come from</p>
          <h2 className="ws-display text-4xl md:text-6xl leading-tight font-light max-w-3xl mx-auto" style={{ color: '#1F2A1A' }}>
            <span className="ws-display-italic" style={{ color: '#A04E3F' }}>Fourteen growers,</span>
            <br /> all within a day's drive.
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-base md:text-lg" style={{ color: '#3E4A3D' }}>
            Every stem we sell — except a handful of off-season specialty cuts — comes from one of these farms.
            Hover any pin.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-8 lg:gap-14 items-center">
          <div className="relative">
            <FarmMap active={active} setActive={setActive} />
          </div>

          <div className="ws-display">
            <p className="ws-eyebrow text-xs" style={{ color: '#A04E3F' }}>
              Farm No. {String(farm.id).padStart(2, '0')} of 14
            </p>
            <h3 className="ws-display text-4xl md:text-5xl font-light leading-tight mt-2" style={{ color: '#1F2A1A' }}>
              {farm.name}
            </h3>
            <p className="ws-display-italic text-xl mt-2" style={{ color: '#A04E3F' }}>
              {farm.city}
            </p>
            <p className="mt-5 text-base leading-relaxed" style={{ color: '#3E4A3D' }}>
              {farm.signature}.
            </p>

            <div className="mt-7 flex flex-wrap gap-2">
              {FARMS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setActive(f.id)}
                  className={`w-7 h-7 rounded-full text-xs font-semibold border transition-all ${
                    f.id === active ? 'bg-[#A04E3F] text-white border-[#A04E3F]' : 'border-[#A04E3F]/40 text-[#A04E3F] hover:bg-[#A04E3F]/10'
                  }`}
                  aria-label={`Farm ${f.id}: ${f.name}`}
                >
                  {f.id}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FarmMap({ active, setActive }) {
  // Stylized PNW outline. Not literal geography — a watercolor impression.
  return (
    <svg viewBox="0 0 480 480" className="w-full max-w-xl mx-auto" aria-label="Pacific Northwest farm map">
      <defs>
        <linearGradient id="ws-or" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#E5DEC8" />
          <stop offset="100%" stopColor="#C9C2A8" />
        </linearGradient>
        <linearGradient id="ws-wa" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#D9DBC0" />
          <stop offset="100%" stopColor="#B5C09A" />
        </linearGradient>
      </defs>

      <path
        d="M 90 30 Q 120 24 160 28 L 220 30 Q 260 28 310 32 L 360 36 L 410 50 Q 422 80 418 110 L 410 130 Q 360 132 310 128 L 250 124 Q 200 130 150 128 L 100 124 Q 80 100 82 70 Z"
        fill="url(#ws-wa)"
        stroke="#7C8771"
        strokeWidth="1.5"
      />
      <text x="320" y="80" fontSize="14" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fill="#566F58" letterSpacing="3">
        Washington
      </text>

      <path
        d="M 90 140 Q 100 136 130 138 L 200 140 Q 280 138 360 144 L 410 152 Q 420 200 414 250 Q 410 320 400 380 L 380 430 Q 350 440 300 442 L 230 444 Q 180 442 140 440 Q 110 432 95 410 Q 85 360 88 310 Q 88 260 90 200 Z"
        fill="url(#ws-or)"
        stroke="#A6B49C"
        strokeWidth="1.5"
      />
      <text x="280" y="220" fontSize="14" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fill="#7C2A1F" letterSpacing="3">
        Oregon
      </text>

      <path d="M 75 50 Q 78 200 70 350 Q 65 400 80 440" fill="none" stroke="#A6B49C" strokeWidth="1" strokeDasharray="2 4" opacity="0.5" />
      <text x="50" y="240" fontSize="9" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fill="#7C8771" transform="rotate(-90 50 240)">
        Pacific Ocean
      </text>

      <path d="M 200 60 L 210 130 L 210 250 L 220 380" fill="none" stroke="#A6B49C" strokeWidth="0.8" opacity="0.7" />
      <text x="178" y="200" fontSize="8" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fill="#566F58" transform="rotate(-85 178 200)" opacity="0.6">
        Cascades
      </text>

      <line x1="0" y1="135" x2="480" y2="135" stroke="#7C8771" strokeWidth="0.4" strokeDasharray="2 3" opacity="0.5" />
      <text x="425" y="132" fontSize="7" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fill="#7C8771" textAnchor="end">
        WA · OR
      </text>

      {FARMS.map((f) => {
        const isActive = f.id === active;
        return (
          <g key={f.id} onClick={() => setActive(f.id)} onMouseEnter={() => setActive(f.id)} style={{ cursor: 'pointer' }}>
            <circle cx={f.x} cy={f.y} r={isActive ? 9 : 5} fill={isActive ? '#A04E3F' : '#C97064'} stroke="#F8F4ED" strokeWidth="1.5" />
            <text x={f.x} y={f.y + 3} textAnchor="middle" fontSize={isActive ? 8 : 6} fontFamily="Cormorant Garamond, serif" fontWeight="700" fill="#F8F4ED">
              {f.id}
            </text>
            {isActive && (
              <g>
                <circle cx={f.x} cy={f.y} r="14" fill="none" stroke="#A04E3F" strokeWidth="1" opacity="0.6" />
                <line x1={f.x + 12} y1={f.y - 12} x2={f.x + 50} y2={f.y - 24} stroke="#A04E3F" strokeWidth="0.7" />
                <text x={f.x + 53} y={f.y - 23} fontSize="10" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fill="#1F2A1A">
                  {f.name}
                </text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function Story({ about, cover }) {
  const body = (about?.body || '').split(/\n\n+/);
  return (
    <section className="relative py-20 md:py-28" style={{ background: '#F0EAD8' }}>
      <div className="max-w-6xl mx-auto px-4 md:px-6 grid md:grid-cols-[1fr_1.3fr] gap-10 md:gap-16 items-start">
        <div className="md:sticky md:top-24">
          <p className="ws-eyebrow mb-2" style={{ color: '#A04E3F' }}>Founder's letter</p>
          <h2 className="ws-display text-4xl md:text-5xl font-light leading-[1.05]" style={{ color: '#1F2A1A' }}>
            <span className="ws-display-italic" style={{ color: '#A04E3F' }}>{about?.heading}</span>
          </h2>

          <div className="mt-8 aspect-[3/4] overflow-hidden">
            <img src={cover} alt="" className="w-full h-full object-cover" loading="lazy" />
          </div>
        </div>

        <div className="ws-display">
          {body.map((para, i) => (
            <p
              key={i}
              className={`leading-[1.75] mb-6 text-[18px] md:text-[19px] ${
                i === 0 ? 'first-letter:text-7xl first-letter:font-light first-letter:float-left first-letter:leading-[0.85] first-letter:mr-3 first-letter:mt-1 first-letter:text-[#A04E3F] first-letter:font-serif' : ''
              }`}
              style={{ color: '#1F2A1A', fontFamily: 'Cormorant Garamond, serif' }}
            >
              {para}
            </p>
          ))}

          {about?.signature && (
            <p className="ws-display-italic text-xl mt-8" style={{ color: '#A04E3F' }}>
              — {about.signature}
            </p>
          )}

          <div className="mt-10 ws-divider" />
        </div>
      </div>
    </section>
  );
}

function WeddingPortfolio({ gallery }) {
  const [active, setActive] = useState(0);
  const items = WEDDING_LOOKS.map((w, i) => ({
    ...w,
    src: gallery?.[w.image % (gallery?.length || 1)] || gallery?.[0],
    idx: i,
  }));

  return (
    <section id="weddings" className="relative py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid md:grid-cols-2 gap-6 md:gap-10 items-end mb-12">
          <div>
            <p className="ws-eyebrow mb-2" style={{ color: '#A04E3F' }}>Weddings & Events</p>
            <h2 className="ws-display text-4xl md:text-6xl leading-tight font-light" style={{ color: '#1F2A1A' }}>
              Four moods we are
              <br /> <span className="ws-display-italic" style={{ color: '#A04E3F' }}>especially good at.</span>
            </h2>
          </div>
          <p className="text-base md:text-lg leading-relaxed" style={{ color: '#3E4A3D' }}>
            Booking 6–12 months out for May–October. Full-service: bouquets, ceremony installs, tablescapes,
            and day-of styling. Always your wedding, never our portfolio piece.
          </p>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {items.map((it, i) => {
            const span =
              it.span === 'lg'
                ? 'col-span-12 md:col-span-7 row-span-2 aspect-[3/4]'
                : it.span === 'md'
                ? 'col-span-6 md:col-span-5 aspect-[4/3]'
                : 'col-span-6 md:col-span-5 md:col-start-8 aspect-square';
            const isActive = active === i;
            return (
              <motion.button
                key={it.tone}
                type="button"
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                whileHover={{ scale: 1.005 }}
                className={`relative overflow-hidden ${span} group`}
                aria-label={`View ${it.tone}`}
              >
                <img
                  src={it.src}
                  alt={it.tone}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className={`absolute inset-0 transition-opacity duration-500 ${isActive ? 'bg-[#1F2A1A]/70' : 'bg-[#1F2A1A]/30'}`} />
                <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-7 text-left text-[#F4ECE0]">
                  <p className="ws-eyebrow text-xs" style={{ color: '#F1B4B0' }}>Mood No. {String(i + 1).padStart(2, '0')}</p>
                  <h3 className="ws-display text-2xl md:text-3xl font-light mt-1">{it.tone}</h3>
                  <div className="flex gap-2 mt-3">
                    {it.palette.map((p, k) => (
                      <span key={k} className="w-4 h-4 rounded-full ring-1 ring-white/40" style={{ background: p }} />
                    ))}
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <a href="#visit" className="inline-flex items-center gap-2 ws-display-italic text-xl hover:underline" style={{ color: '#A04E3F' }}>
            Book a wedding consult <ArrowRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
}

function Services() {
  const [tier, setTier] = useState('designer');
  const [frequency, setFrequency] = useState('Monthly');
  const t = SUBSCRIPTIONS.find((s) => s.id === tier);
  const multiplier = frequency === 'Weekly' ? 4 : frequency === 'Bi-weekly' ? 2 : 1;
  const monthly = t.price * multiplier;
  const yearly = monthly * 12;
  const [submitted, setSubmitted] = useState(false);

  return (
    <section id="services" className="relative py-20 md:py-28" style={{ background: '#1F2A1A', color: '#F4ECE0' }}>
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <p className="ws-eyebrow mb-2" style={{ color: '#F1B4B0' }}>Subscriptions</p>
          <h2 className="ws-display text-4xl md:text-6xl leading-tight font-light">
            Flowers on a <span className="ws-display-italic" style={{ color: '#F1B4B0' }}>standing order.</span>
          </h2>
          <p className="mt-4 max-w-xl mx-auto opacity-80">
            Designer's choice from what's freshest that week. Cancel anytime, 10% off on everyday orders for subscribers.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mb-10">
          {SUBSCRIPTIONS.map((s) => {
            const selected = tier === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setTier(s.id)}
                className={`text-left p-7 transition-all border-2 ${
                  selected ? 'border-[#F1B4B0] bg-[#F1B4B0]/5' : 'border-[#3E4A3D] hover:border-[#F1B4B0]/60'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="ws-display text-2xl md:text-3xl font-light">{s.name}</h3>
                  {s.pop && (
                    <span className="ws-eyebrow text-xs px-2 py-1" style={{ color: '#F1B4B0', background: 'rgba(241,180,176,0.15)' }}>
                      Most loved
                    </span>
                  )}
                </div>
                <p className="text-sm opacity-80 mt-3 leading-relaxed">{s.desc}</p>
                <p className="ws-display text-3xl mt-5" style={{ color: selected ? '#F1B4B0' : '#F4ECE0' }}>
                  ${s.price}
                  <span className="ws-display-italic text-base opacity-70"> / delivery</span>
                </p>
              </button>
            );
          })}
        </div>

        <div className="rounded-none p-7 md:p-9 grid md:grid-cols-[1.4fr_1fr] gap-7 md:gap-12 items-center" style={{ background: '#3E4A3D' }}>
          <div>
            <p className="ws-eyebrow text-xs mb-3" style={{ color: '#F1B4B0' }}>Frequency</p>
            <div className="flex flex-wrap gap-2">
              {['Weekly', 'Bi-weekly', 'Monthly'].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFrequency(f)}
                  className={`px-4 py-2.5 ws-display text-base transition-all border ${
                    frequency === f ? 'bg-[#F1B4B0] text-[#1F2A1A] border-[#F1B4B0]' : 'border-[#566F58] hover:border-[#F1B4B0]'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>

            <p className="mt-5 ws-display-italic text-lg leading-relaxed opacity-90">
              {frequency === 'Weekly'
                ? 'A bouquet on the desk every Monday morning.'
                : frequency === 'Bi-weekly'
                ? "Two bouquets a month — one to start, one to refresh."
                : 'One bouquet a month, delivered on the first Friday.'}
            </p>
          </div>

          <div className="md:border-l md:pl-12" style={{ borderColor: 'rgba(241,180,176,0.25)' }}>
            <p className="ws-eyebrow text-xs" style={{ color: '#F1B4B0' }}>{frequency} total</p>
            <p className="ws-display text-5xl mt-1">${monthly}<span className="ws-display-italic text-lg opacity-70"> / mo</span></p>
            <p className="text-sm opacity-70 mt-1">${yearly} a year · cancel anytime</p>

            <button
              type="button"
              onClick={() => { setSubmitted(true); setTimeout(() => setSubmitted(false), 4500); }}
              disabled={submitted}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 ws-display text-lg font-medium transition-all hover:bg-[#7C2A1F] disabled:opacity-70"
              style={{ background: '#A04E3F', color: '#F8F4ED', borderRadius: 0 }}
            >
              {submitted ? <><Check size={18} /> We'll confirm by email</> : <>Start subscription <ArrowRight size={18} /></>}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Workshops() {
  return (
    <section id="workshops" className="relative py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid md:grid-cols-2 gap-8 md:gap-14 items-end mb-12">
          <div>
            <p className="ws-eyebrow mb-2" style={{ color: '#A04E3F' }}>Workshops</p>
            <h2 className="ws-display text-4xl md:text-6xl leading-tight font-light" style={{ color: '#1F2A1A' }}>
              Learn at the <span className="ws-display-italic" style={{ color: '#A04E3F' }}>studio bench.</span>
            </h2>
          </div>
          <p className="text-base md:text-lg leading-relaxed" style={{ color: '#3E4A3D' }}>
            Two-hour seasonal classes, capped at 12. Wine, take-home flowers, and a paper recipe card.
            $95 per seat.
          </p>
        </div>

        <ul className="divide-y" style={{ borderColor: '#E0D6C2' }}>
          {WORKSHOPS.map((w) => (
            <li key={w.title} className="grid grid-cols-[auto_1fr_auto] items-center gap-4 md:gap-8 py-5 border-t" style={{ borderColor: '#E0D6C2' }}>
              <div className="ws-display-italic text-xl md:text-2xl whitespace-nowrap" style={{ color: '#A04E3F' }}>
                {w.date}
              </div>
              <div>
                <p className="ws-display text-lg md:text-xl" style={{ color: '#1F2A1A' }}>{w.title}</p>
                <p className="text-xs uppercase tracking-wider mt-1" style={{ color: w.full ? '#A04E3F' : '#7C8771' }}>
                  {w.full ? 'Sold out — join waitlist' : `${w.open} of ${w.total} seats open`}
                </p>
              </div>
              <button
                type="button"
                disabled={w.full}
                className={`ws-display text-base px-5 py-2.5 transition-all whitespace-nowrap ${
                  w.full ? 'opacity-50 cursor-not-allowed border border-[#A04E3F]/30' : 'border border-[#A04E3F] text-[#A04E3F] hover:bg-[#A04E3F] hover:text-[#F8F4ED]'
                }`}
                style={{ borderRadius: 0 }}
              >
                {w.full ? 'Waitlist' : 'Reserve'}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Sympathy() {
  return (
    <section className="py-16 md:py-20" style={{ background: '#F0EAD8' }}>
      <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
        <p className="ws-eyebrow mb-2" style={{ color: '#A04E3F' }}>Sympathy & Tributes</p>
        <h2 className="ws-display text-3xl md:text-4xl leading-tight font-light mb-5" style={{ color: '#1F2A1A' }}>
          When the moment <span className="ws-display-italic" style={{ color: '#A04E3F' }}>asks for flowers,</span>
          <br /> we are gentle, fast, and quiet about it.
        </h2>
        <p className="text-base md:text-lg leading-relaxed mb-7 max-w-xl mx-auto" style={{ color: '#3E4A3D' }}>
          Same-day delivery throughout Portland. Thoughtful, modern arrangements — never a styrofoam ring,
          never a stock catalog. Call directly and we'll talk through what feels right.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-sm" style={{ color: '#7C8771' }}>
          <span className="flex items-center gap-1.5"><Clock size={14} /> Order by 11 AM for same-day</span>
          <span className="flex items-center gap-1.5"><Heart size={14} /> Direct phone line</span>
          <span className="flex items-center gap-1.5"><Leaf size={14} /> Compostable wrap</span>
        </div>
      </div>
    </section>
  );
}

function Testimonials({ testimonials }) {
  const [index, setIndex] = useState(0);
  const total = testimonials?.length || 0;

  useEffect(() => {
    if (total === 0) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % total), 7000);
    return () => clearInterval(id);
  }, [total]);

  if (!testimonials?.length) return null;
  const t = testimonials[index];

  return (
    <section className="relative py-20 md:py-28">
      <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
        <p className="ws-eyebrow mb-3" style={{ color: '#A04E3F' }}>Words left in the mailbox</p>

        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4 }}
          >
            <p className="ws-display text-2xl md:text-4xl leading-snug font-light" style={{ color: '#1F2A1A' }}>
              &ldquo;{t.quote}&rdquo;
            </p>
            <p className="ws-display-italic text-xl mt-7" style={{ color: '#A04E3F' }}>— {t.author}</p>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-center gap-2 mt-9">
          {testimonials.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Quote ${i + 1}`}
              className="transition-all"
              style={{
                width: i === index ? 28 : 6,
                height: 6,
                borderRadius: 3,
                background: i === index ? '#A04E3F' : 'rgba(160,78,63,0.3)',
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function Visit({ demo, phoneRaw }) {
  return (
    <section id="visit" className="relative py-20 md:py-28" style={{ background: '#F0EAD8' }}>
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16">
          <div>
            <p className="ws-eyebrow mb-2" style={{ color: '#A04E3F' }}>Visit the studio</p>
            <h2 className="ws-display text-4xl md:text-5xl leading-tight font-light" style={{ color: '#1F2A1A' }}>
              The shop on <span className="ws-display-italic" style={{ color: '#A04E3F' }}>NW 23rd</span>.
            </h2>
            <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: '#3E4A3D' }}>
              The doors are blue. The bell rings when you push them open. There's usually a dog
              under the back table. Walk-ins welcome — appointments preferred for weddings or anything
              over an arrangement.
            </p>

            <ul className="mt-8 space-y-4">
              <li className="flex items-start gap-3">
                <Phone size={18} className="flex-shrink-0 mt-1" style={{ color: '#A04E3F' }} />
                <div>
                  <div className="ws-eyebrow text-xs" style={{ color: '#7C8771' }}>Phone</div>
                  <a href={`tel:${phoneRaw}`} className="ws-display text-xl hover:underline" style={{ color: '#1F2A1A' }}>
                    {demo.contact.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={18} className="flex-shrink-0 mt-1" style={{ color: '#A04E3F' }} />
                <div>
                  <div className="ws-eyebrow text-xs" style={{ color: '#7C8771' }}>Email</div>
                  <a href={`mailto:${demo.contact.email}`} className="ws-display text-xl hover:underline break-all" style={{ color: '#1F2A1A' }}>
                    {demo.contact.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={18} className="flex-shrink-0 mt-1" style={{ color: '#A04E3F' }} />
                <div>
                  <div className="ws-eyebrow text-xs" style={{ color: '#7C8771' }}>Studio</div>
                  <p className="ws-display text-xl" style={{ color: '#1F2A1A' }}>{demo.contact.address}</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="border-l-0 lg:border-l lg:pl-14" style={{ borderColor: '#E0D6C2' }}>
            <p className="ws-eyebrow mb-3" style={{ color: '#A04E3F' }}>Hours</p>
            <ul>
              {demo.hours.map((h) => (
                <li key={h.day} className="grid grid-cols-2 gap-3 py-4 border-b items-baseline" style={{ borderColor: '#E0D6C2' }}>
                  <span className="ws-display text-lg flex items-center gap-2" style={{ color: '#1F2A1A' }}>
                    <Calendar size={14} style={{ color: '#A04E3F' }} /> {h.day}
                  </span>
                  <span className="ws-display-italic text-lg" style={{ color: '#3E4A3D' }}>{h.time}</span>
                </li>
              ))}
            </ul>

            <p className="mt-6 ws-display-italic text-lg" style={{ color: '#3E4A3D' }}>
              Same-day delivery in Portland for orders placed by 11 AM.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer({ demo, phoneRaw }) {
  return (
    <footer className="py-14 md:py-16" style={{ background: '#1F2A1A', color: '#A6B49C' }}>
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid md:grid-cols-[1.3fr_1fr_1fr] gap-10 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[#F1B4B0]"><Leaf size={20} /></span>
              <span className="ws-display text-xl text-[#F4ECE0]">{demo.name}</span>
            </div>
            <p className="ws-display-italic text-lg leading-relaxed text-[#A6B49C] max-w-sm">
              PNW-grown, hand-designed, never wholesale-flown.
            </p>
            <div className="ws-divider mt-5" style={{ background: '#A04E3F' }} />
          </div>

          <div className="ws-display">
            <p className="ws-eyebrow text-xs mb-3" style={{ color: '#F1B4B0' }}>Studio</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2"><MapPin size={14} className="mt-1 flex-shrink-0" /> {demo.contact.address}</li>
              <li className="flex items-start gap-2"><Phone size={14} className="mt-1 flex-shrink-0" /> <a href={`tel:${phoneRaw}`} className="hover:text-[#F1B4B0]">{demo.contact.phone}</a></li>
              <li className="flex items-start gap-2"><Mail size={14} className="mt-1 flex-shrink-0" /> <a href={`mailto:${demo.contact.email}`} className="hover:text-[#F1B4B0] break-all">{demo.contact.email}</a></li>
            </ul>
          </div>

          <div className="ws-display">
            <p className="ws-eyebrow text-xs mb-3" style={{ color: '#F1B4B0' }}>Wander to</p>
            <ul className="space-y-2">
              <li><a href="#calendar" className="hover:text-[#F1B4B0]">In season this week</a></li>
              <li><a href="#studio" className="hover:text-[#F1B4B0]">Build a bouquet</a></li>
              <li><a href="#farms" className="hover:text-[#F1B4B0]">Our 14 farms</a></li>
              <li><a href="#weddings" className="hover:text-[#F1B4B0]">Weddings</a></li>
              <li><a href="#workshops" className="hover:text-[#F1B4B0]">Workshops</a></li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-3 text-xs" style={{ borderColor: '#3E4A3D' }}>
          <p className="opacity-70">&copy; {new Date().getFullYear()} {demo.name}. Designed by hand, on paper.</p>
          <p>
            Demo site built by{' '}
            <Link to="/" className="ws-display-italic hover:underline" style={{ color: '#F1B4B0' }}>Sudco Solutions</Link>
          </p>
        </div>
      </div>
    </footer>
  );
}

export default WildStemFloralDemo;
